import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Text, Callout, Intent, Button } from '@blueprintjs/core';
import { githubApi } from '../../api/github';

/**
 * GitHub OAuth callback handler
 * Handles the GitHub OAuth redirect and forwards to the integration landing page
 */
const GithubOAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processInstallation = async () => {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const installationId = urlParams.get('installation_id');
      const setupAction = urlParams.get('setup_action');

      console.log('[GithubOAuthCallback] useEffect triggered.', {
        pathname: location.pathname,
        search: location.search,
        code,
        installationId,
        setupAction
      });

      const redirectParams = new URLSearchParams();

      if (setupAction === 'install' && installationId) {
        try {
          setProcessing(true);
          setError(null);
          console.log('[GithubOAuthCallback] Attempting to complete installation with backend for ID:', installationId);
          const response = await githubApi.completeInstallation(installationId);
          console.log('[GithubOAuthCallback] Backend response from completeInstallation:', response);

          if (response && response.success) {
            redirectParams.set('installed', '1');
            redirectParams.set('installation_id', installationId);
          } else {
            redirectParams.set('installed', '0');
            if (installationId) redirectParams.set('installation_id', installationId);
            const message = response?.message || 'Backend failed to confirm installation.';
            setError(`GitHub installation failed: ${message}`);
            console.error('[GithubOAuthCallback] Installation completion failed:', message);
          }
        } catch (err: any) {
          console.error('[GithubOAuthCallback] Error calling completeInstallation:', err);
          setError(`Error during GitHub installation: ${err.message || 'Unknown error'}`);
          redirectParams.set('installed', '0');
          if (installationId) redirectParams.set('installation_id', installationId);
          if (err.message) redirectParams.set('error', err.message);
        } finally {
          setProcessing(false);
        }
      } else if (code) {
        // This is an OAuth code callback (e.g., for user auth, less relevant here)
        // For now, just pass it along if it exists
        redirectParams.set('code', code);
        if (installationId) {
          redirectParams.set('installation_id', installationId);
        }
        setProcessing(false); // Not processing an install here
      } else {
        // Neither install nor code, likely direct navigation or missing params
        setError('Invalid GitHub callback: Missing required parameters.');
        setProcessing(false);
        // No redirect params set, will fall through to navigate with empty params or show error
      }

      // Only navigate if we are not showing a persistent error here
      // If an error occurred during backend communication, the error state will be set,
      // and the UI below will render it. The user can then manually navigate.
      // If processing is false AND no error, then we can proceed with navigation.
      if (!processing && !error) { 
        const targetUrl = `/integrations/github?${redirectParams.toString()}`;
        console.log('[GithubOAuthCallback] Navigating to:', targetUrl);
        navigate(targetUrl, { replace: true });
      }
    };

    processInstallation();

  }, [navigate, location.search]);

  if (processing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
        <Spinner size={40} />
        <Text>Processing GitHub integration...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: 20 }}>
        <Callout intent={Intent.DANGER} icon="error" title="GitHub Integration Error">
          <p>{error}</p>
          <Button style={{marginTop: '15px'}} onClick={() => navigate('/integrations', { replace: true })}>Go to Integrations</Button>
        </Callout>
      </div>
    );
  }

  // Fallback or if navigation hasn't occurred yet (should be rare if no error and not processing)
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
      <Text>Finalizing GitHub setup...</Text>
      {/* This state should ideally not be reached if logic is correct, implies waiting for navigation */} 
    </div>
  );
};

export default GithubOAuthCallback; 